// ============================================
// Seed Templates: Vendor & Partner Agreements
// Category: vendor-partner
// Templates: 6
// ============================================

import type { DocTemplate } from '../../types/docops';

export const SEED_TEMPLATES_VENDOR: DocTemplate[] = [
  // ── 6.1 OpenLoop Telehealth Agreement ──
  {
    id: '6.1-openloop-agreement',
    categoryId: 'vendor-partner',
    track: 'reference',
    title: 'OpenLoop Telehealth Agreement',
    description:
      'Reference placeholder for the vendor-provided telehealth agreement. Upload the standard agreement from the telehealth network for tracking and review.',
    templateContent: `# OpenLoop Telehealth Agreement

**{{brandEntity}}** ↔ **{{telehealthNetwork}}**

Clinical Services Entity: {{pcEntityName}}

_{{telehealthNetwork}} provides their standard agreement. Upload the vendor-provided agreement for tracking and review._

## Key Terms to Verify
- Consultation pricing
- State coverage
- Provider qualifications
- BAA inclusion
- SLA for response times
- Integration requirements
`,
    requiredVariables: [
      'brandEntity',
      'telehealthNetwork',
      'pcEntityName',
    ],
    generationPrompt:
      '{{telehealthNetwork}} provides their standard agreement. This is a reference placeholder — upload the vendor-provided agreement for tracking and review. Key terms to verify: consultation pricing, state coverage, provider qualifications, BAA inclusion, SLA for response times, integration requirements.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 0,
    sortOrder: 1,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 6.2 Pharmacy Partnership Agreement ──
  {
    id: '6.2-pharmacy-agreement',
    categoryId: 'vendor-partner',
    title: 'Pharmacy Partnership Agreement',
    description:
      'Partnership agreement outline with compounding pharmacy covering revenue share, quality standards, fulfillment SLAs, and regulatory compliance.',
    templateContent: `# Pharmacy Partnership Agreement

**{{brandEntity}}** ↔ **Pharmacy Partner**

## Revenue Share Terms
{{AI_GENERATED_CONTENT}}

## Quality Standards and Certifications
{{AI_GENERATED_CONTENT}}

## Fulfillment SLA
{{AI_GENERATED_CONTENT}}

## Returns and Recalls
{{AI_GENERATED_CONTENT}}

## Insurance Requirements
{{AI_GENERATED_CONTENT}}

## Regulatory Compliance
{{AI_GENERATED_CONTENT}}

## Termination Provisions
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'pharmacyPartner',
      'legalEmail',
    ],
    generationPrompt:
      'Generate Pharmacy Partnership Agreement outline for {{brandEntity}} and compounding pharmacy partner. Cover: revenue share terms, quality standards and certifications required, BAA obligations, fulfillment SLA (ship within 48 hours of prescription approval), returns and recalls policy, insurance requirements, regulatory compliance (state pharmacy board licenses), data sharing requirements, termination provisions. Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 12,
    sortOrder: 2,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 6.3 Supplement Fulfillment Agreement ──
  {
    id: '6.3-supplement-fulfillment',
    categoryId: 'vendor-partner',
    title: 'Supplement Fulfillment Agreement',
    description:
      'Fulfillment agreement outline covering wholesale pricing, shipping SLAs, quality control, product liability, and labeling requirements.',
    templateContent: `# Supplement Fulfillment Agreement

**{{brandEntity}}** ↔ **Fulfillment Partner**

## Wholesale Pricing Structure
{{AI_GENERATED_CONTENT}}

## Shipping SLA and Methods
{{AI_GENERATED_CONTENT}}

## Quality Control Requirements
{{AI_GENERATED_CONTENT}}

## Product Liability and Insurance
{{AI_GENERATED_CONTENT}}

## Returns Handling
{{AI_GENERATED_CONTENT}}

## Labeling Requirements
{{AI_GENERATED_CONTENT}}

## Regulatory Compliance
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'fulfillmentPartner',
      'legalEmail',
    ],
    generationPrompt:
      'Generate Supplement Fulfillment Agreement outline for {{brandEntity}}. Cover: wholesale pricing structure, shipping SLA and methods, quality control requirements (cGMP, third-party testing), product liability and insurance, returns handling, inventory management, labeling requirements, regulatory compliance. Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 3,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 6.4 Stripe Services Agreement ──
  {
    id: '6.4-stripe-agreement',
    categoryId: 'vendor-partner',
    track: 'reference',
    title: 'Stripe Services Agreement',
    description:
      'Reference placeholder for Stripe standard terms of service. No generation needed — link to standard agreement and note acceptance date.',
    templateContent: `# Stripe Services Agreement

**{{brandEntity}}** ↔ **{{paymentProcessor}}**

_{{paymentProcessor}} uses standard terms of service. No generation needed._

## Reference
- Stripe Services Agreement: https://stripe.com/legal/ssa
- Acceptance Date: To be recorded upon execution

_Upload confirmation of terms acceptance for tracking._
`,
    requiredVariables: [
      'brandEntity',
      'paymentProcessor',
    ],
    generationPrompt: `{{paymentProcessor}} uses standard terms of service. This is a reference placeholder document for {{brandEntity}}.

Generate a brief reference document that includes:
1. Statement that {{paymentProcessor}} provides standard terms of service at https://stripe.com/legal/ssa
2. Key terms for {{brandEntity}} to verify: PCI DSS compliance, chargeback policies, payout schedules, prohibited business types, healthcare payment compliance
3. Internal tracking fields: acceptance date, account ID, primary contact, integration type (Stripe Checkout)
4. Note that {{brandEntity}} should upload confirmation of terms acceptance for compliance tracking

FORMAT RULES:
- Use actual entity names ({{brandEntity}}, {{paymentProcessor}}) — NEVER bracket placeholders like [X] or [Record here]
- For fields to be filled later, use "To be recorded" or "Pending"
- Do NOT include meta-commentary or notes about the generation process
- Keep it concise — this is a 1-page reference document`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 0,
    sortOrder: 4,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 6.5 Data Processing Agreement (AI/Cloud) ──
  {
    id: '6.5-data-processing-agreement',
    categoryId: 'vendor-partner',
    title: 'Data Processing Agreement (AI/Cloud)',
    description:
      'Data processing agreement for AI and cloud service providers covering data processing scope, security measures, breach notification, and compliance with MHMDA and state privacy laws.',
    templateContent: `# Data Processing Agreement

**{{brandEntity}}** — Data Controller

## Data Processing Scope
{{AI_GENERATED_CONTENT}}

## Data Categories Processed
{{AI_GENERATED_CONTENT}}

## Processing Purposes
{{AI_GENERATED_CONTENT}}

## Subprocessor Obligations
{{AI_GENERATED_CONTENT}}

## Security Measures
{{AI_GENERATED_CONTENT}}

## Breach Notification
{{AI_GENERATED_CONTENT}}

## Data Subject Rights Fulfillment
{{AI_GENERATED_CONTENT}}

## Audit Rights
{{AI_GENERATED_CONTENT}}

## Data Deletion on Termination
{{AI_GENERATED_CONTENT}}

Contact: {{legalEmail}} | {{privacyEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'cloudProvider',
      'legalEmail',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate Data Processing Agreement for {{brandEntity}} with AI and cloud service providers. Covers: data processing scope (lab data analysis via Claude API, data storage via {{cloudProvider}}), data categories processed, processing purposes, subprocessor obligations, data transfer mechanisms, security measures, breach notification, data subject rights fulfillment, audit rights, data deletion on termination. Compliant with MHMDA and state privacy laws. Format: complete agreement template.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 5,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 6.6 Firebase/GCP Terms & BAA ──
  {
    id: '6.6-firebase-gcp-terms',
    categoryId: 'vendor-partner',
    track: 'reference',
    title: 'Firebase/GCP Terms & BAA',
    description:
      'Reference placeholder for Google Cloud/Firebase standard terms and BAA. Track BAA execution date, covered services, and compliance contact.',
    templateContent: `# Firebase/GCP Terms & BAA

**{{brandEntity}}** ↔ **{{cloudProvider}}**

_{{cloudProvider}} uses standard terms. No generation needed._

## Tracking
- BAA Executed Date: [Record date here]
- Covered Services: [List HIPAA-eligible services configured]
- Compliance Contact: [Record contact here]

## Reference
- Google Cloud Terms: https://cloud.google.com/terms
- Firebase Terms: https://firebase.google.com/terms
- Google Cloud BAA: https://cloud.google.com/terms/business-associate-agreement

_Upload confirmation of BAA acceptance for tracking._
`,
    requiredVariables: [
      'brandEntity',
      'cloudProvider',
    ],
    generationPrompt:
      '{{cloudProvider}} uses standard terms. This is a reference placeholder — note Google Cloud BAA acceptance date, Firebase terms acceptance, HIPAA-eligible services configured. Track: BAA executed date, covered services list, compliance contact.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 0,
    sortOrder: 6,
    generationRound: 5,
    dependencies: ['5.1-baa-llc-pc', '1.2-privacy-policy'],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
